/*
  Vanilla replacement for Foundation Accordion.
  Supports the existing markup: [data-accordion] > [data-accordion-item]
  with .accordion-title and .accordion-content[data-tab-content].
*/

const ACTIVE = "is-active";
const SLIDE_SPEED = 250;

let contentIdCounter = 0;

const getTitle = (item) => item.querySelector(".accordion-title");
const getContent = (item) =>
  item.querySelector("[data-tab-content]") ||
  item.querySelector(".accordion-content");

const prefersReducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const COLLAPSED = {
  height: "0px",
  paddingTop: "0px",
  paddingBottom: "0px",
  marginTop: "0px",
  marginBottom: "0px",
};

const slide = (content, open) => {
  content.slideAnimation?.cancel();

  content.style.display = "block";
  const style = window.getComputedStyle(content);
  // Padding and margin have to collapse alongside the height, otherwise the box
  // still reserves space at height 0 and snaps away when display is cleared.
  const expanded = {
    height: `${content.scrollHeight}px`,
    paddingTop: style.paddingTop,
    paddingBottom: style.paddingBottom,
    marginTop: style.marginTop,
    marginBottom: style.marginBottom,
  };

  content.style.overflow = "hidden";
  content.style.willChange = "height";
  content.style.contain = "layout paint";

  const animation = content.animate(
    open ? [COLLAPSED, expanded] : [expanded, COLLAPSED],
    { duration: SLIDE_SPEED, easing: "ease", fill: "forwards" },
  );
  content.slideAnimation = animation;

  animation.finished
    .then(() => {
      content.style.overflow = "";
      content.style.willChange = "";
      content.style.contain = "";
      content.style.display = open ? "block" : "";
      // Release the forwards fill only once the final state is in the inline style.
      animation.cancel();
    })
    .catch(() => {});
};

const setState = (item, open, animate = false) => {
  const title = getTitle(item);
  const content = getContent(item);

  item.classList.toggle(ACTIVE, open);
  if (title) {
    title.setAttribute("aria-expanded", String(open));
  }
  if (!content) {
    return;
  }
  content.classList.toggle(ACTIVE, open);

  if (animate && !prefersReducedMotion()) {
    slide(content, open);
  } else {
    content.style.display = open ? "block" : "";
  }
};

const setupAccordion = (root) => {
  if (root.dataset.accordionReady === "true") {
    return;
  }
  root.dataset.accordionReady = "true";

  const multiExpand = root.dataset.multiExpand === "true";
  const allowAllClosed = root.dataset.allowAllClosed === "true";
  const items = Array.from(root.querySelectorAll("[data-accordion-item]"));

  items.forEach((item, index) => {
    const title = getTitle(item);
    const content = getContent(item);

    if (title && content) {
      if (!content.id) {
        contentIdCounter += 1;
        content.id = `accordion-content-${contentIdCounter}`;
      }
      title.setAttribute("role", "button");
      title.setAttribute("aria-controls", content.id);
      content.setAttribute("role", "region");
    }

    const startOpen =
      item.classList.contains(ACTIVE) ||
      (!allowAllClosed && index === 0 && !root.querySelector(`.${ACTIVE}`));

    setState(item, startOpen);
  });

  root.addEventListener("click", (event) => {
    const title = event.target.closest(".accordion-title");
    if (!title || !root.contains(title)) {
      return;
    }
    event.preventDefault();

    const item = title.closest("[data-accordion-item]");
    const isOpen = item.classList.contains(ACTIVE);

    if (isOpen && !allowAllClosed) {
      return;
    }

    if (!isOpen && !multiExpand) {
      items.forEach((other) => {
        if (other !== item && other.classList.contains(ACTIVE)) {
          setState(other, false, true);
        }
      });
    }

    setState(item, !isOpen, true);
    root.dispatchEvent(
      new CustomEvent("accordion:toggle", {
        bubbles: true,
        detail: { item, open: !isOpen },
      }),
    );
  });
};

export const accordionInit = (scope = document) => {
  scope.querySelectorAll("[data-accordion]").forEach(setupAccordion);
};

export default accordionInit;
